import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUIStore } from "@/store/ui.store";
import { useAuthStore } from "@/store/auth.store";
import { getPrimaryTalentChekRole } from "@/utils/roles.util";

const MoonIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
    />
  </svg>
);

const SunIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

const MenuIcon = () => (
  <svg
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const GlobeIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 014 9 15 15 0 01-4 9 15 15 0 01-4-9 15 15 0 014-9z"
    />
  </svg>
);

/** Derive display name – prefers /v2/users/me profile, then V2 login, then V1 AuthUser */
function useDisplayName(): {
  displayName: string;
  initials: string;
  employeeRole: string;
} {
  const user = useAuthStore((s) => s.user);
  const v2User = useAuthStore((s) => s.v2User);
  const userProfile = useAuthStore((s) => s.userProfile);
  const authority = useAuthStore((s) => s.authority);

  const derivedRole =
    (authority ? getPrimaryTalentChekRole(authority.roles) : undefined) ||
    v2User?.user_type ||
    user?.employeeRole ||
    "User";

  // Prefer /v2/users/me profile (has real first name)
  if (userProfile) {
    const first = userProfile.firstname ?? "";
    const last = userProfile.lastname ?? "";
    return {
      displayName: first || `${first} ${last}`.trim() || "User",
      initials: `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase() || "U",
      employeeRole: derivedRole,
    };
  }

  if (v2User) {
    const first = v2User.first_name ?? "";
    const last = v2User.last_name ?? "";
    return {
      displayName: `${first} ${last}`.trim() || "User",
      initials: `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase() || "U",
      employeeRole: derivedRole,
    };
  }

  if (user) {
    return {
      displayName: `${user.firstName} ${user.lastName}`,
      initials: `${user.firstName[0] ?? ""}${
        user.lastName[0] ?? ""
      }`.toUpperCase(),
      employeeRole: derivedRole,
    };
  }

  return { displayName: "Guest User", initials: "GU", employeeRole: "Viewer" };
}

export const Topbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { toggleTheme, theme, toggleSidebar } = useUIStore();
  const { displayName, initials, employeeRole } = useDisplayName();
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language?.startsWith("fr") ? "fr" : "en";

  const switchLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setLangDropdownOpen(false);
  };

  // Close language dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangDropdownOpen(false);
      }
    };
    if (langDropdownOpen) {
      document.addEventListener("pointerdown", handleClickOutside);
    }
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [langDropdownOpen]);

  return (
    <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6 dark:border-gray-800 dark:bg-gray-900 flex-shrink-0">
      <div className="flex items-center gap-3">
        <button
          onClick={toggleSidebar}
          className="rounded-lg p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
          aria-label="Toggle sidebar"
        >
          <MenuIcon />
        </button>
        <div className="hidden md:flex flex-col">
          <span className="text-sm font-semibold text-gray-900 dark:text-white leading-none">
            {t("common.appName")}
          </span>
          <span className="text-[11px] text-gray-400 mt-0.5">
            {t("common.poweredBy")}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Language Selector */}
        <div className="relative" ref={langRef}>
          <button
            onClick={() => setLangDropdownOpen(!langDropdownOpen)}
            className="flex items-center gap-1.5 rounded-lg px-2 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors text-xs font-medium"
            aria-label={t("language.label")}
            id="language-selector"
          >
            <GlobeIcon />
            <span className="hidden sm:inline">{currentLang.toUpperCase()}</span>
          </button>

          {langDropdownOpen && (
            <div className="absolute right-0 top-full mt-1 w-36 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 z-50 overflow-hidden">
              <button
                onClick={() => switchLanguage("en")}
                className={`flex w-full items-center gap-2 px-3 py-2.5 text-xs font-medium transition-colors ${
                  currentLang === "en"
                    ? "bg-accent-50 text-accent-600 dark:bg-accent-950/20 dark:text-accent-400"
                    : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
                id="lang-option-en"
              >
                <span className="text-base">🇬🇧</span>
                {t("language.english")}
                {currentLang === "en" && (
                  <svg className="ml-auto h-4 w-4 text-accent-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => switchLanguage("fr")}
                className={`flex w-full items-center gap-2 px-3 py-2.5 text-xs font-medium transition-colors ${
                  currentLang === "fr"
                    ? "bg-accent-50 text-accent-600 dark:bg-accent-950/20 dark:text-accent-400"
                    : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
                id="lang-option-fr"
              >
                <span className="text-base">🇫🇷</span>
                {t("language.french")}
                {currentLang === "fr" && (
                  <svg className="ml-auto h-4 w-4 text-accent-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
          aria-label="Toggle theme"
        >
          {theme === "light" ? <MoonIcon /> : <SunIcon />}
        </button>

        {/* Divider */}
        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />

        {/* User */}
        <div className="flex items-center gap-3 pl-2">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-primary-900 flex-shrink-0"
            style={{ backgroundColor: "#FAA61A" }}
          >
            {initials}
          </div>
          <div className="hidden sm:flex flex-col text-left leading-none">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {displayName}
            </span>
            <span className="text-xs text-gray-400 capitalize mt-0.5">
              {employeeRole}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
